
/**
 * ELAKAY – GraffitiOrderClient.jsx
 *
 * Vue client avec onglets façon GraffitiOrderSeller.
 *
 * Pour mode "ask" :
 *   Tab 1 "Client Request"  → Liste des demandes (max 3) + formulaire ask
 *   Tab 2 "Seller Designs"  → Liste des designs soumis par le seller
 *                              + bouton "View Design" pour chacun
 *                              + actions Approve/Reject (sur le dernier)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { DesignPreviewModal } from "./externPageGraffiti/GraffitiPreview";
import useGraphicOrderSave from "./externPageGraffiti/useGraphicOrderSave";
import {
  CSS_
} from "./externPageGraffiti/DesignStudio";
import { approveSellerDesign, rejectSellerDesign } from "../../auth/apiProduct";
import { isAuthenticated } from "../../auth";
import { elakayAutoText } from "../dataElakay";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["graffitiOrderClient"]?.[key] || fallback;

const MAX_ASK_REQUESTS = 3;

const getHardcodedText = (infoSource, group, key, fallback) =>
  infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.[group]?.[key] || fallback;

const ARTISTIC_STYLES = [
  __elakayText("t073", "Street Art"), __elakayText("t074", "Bubble"), __elakayText("t075", "Wildstyle"), __elakayText("t076", "Calligraphy"), __elakayText("t077", "Minimalist"),
  __elakayText("t078", "Vintage"), __elakayText("t079", "Neon"), __elakayText("t080", "Abstract"), __elakayText("t081", "Cartoon"), __elakayText("t082", "Gothic")
];
const DETAIL_LEVELS = [__elakayText("t083", "Simple"), __elakayText("t084", "Modéré"), __elakayText("t085", "Détaillé"), __elakayText("t086", "Hyper-détaillé")];
const INSPIRATION_STYLES = [
  { label: __elakayText("t087", "Bold & Loud"), icon: "⚡", desc: "Lettres imposantes" },
  { label: __elakayText("t088", "Clean & Modern"), icon: "◻️", desc: "Minimal, géométrique" },
  { label: __elakayText("t089", "Wild & Complex"), icon: "🌀", desc: "Profondeur 3D" },
  { label: __elakayText("t090", "Retro & Vintage"), icon: "📻", desc: "Old school bombe" }
];

/* ══════════════════════════════════════════════════════
   CSS
══════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&family=Space+Mono:wght@400;700&display=swap');

*,*::before,*::after{box-sizing:border-box}

.goc-root{
  --n0:#f2f6fd; --n1:#ffffff; --n2:#f4f8fc; --n3:#eef6fb;
  --n4:#daeaf5; --n5:#c8ddea;
  --primary:#007399; --primary-light:#00a4d8;
  --primary-soft:rgba(0,115,153,.10); --primary-softer:rgba(0,115,153,.05);
  --teal:#4facfe; --teal-light:#00f2fe; --teal-soft:rgba(79,172,254,.10);
  --emerald:#34c759; --emerald-soft:rgba(52,199,89,.08);
  --gold:#c9a227; --gold-soft:rgba(201,162,39,.10);
  --warn:#f59e0b; --warn-soft:rgba(245,158,11,.08);
  --err:#ef4444; --err-soft:rgba(239,68,68,.08);
  --purple:#a855f7; --purple-soft:rgba(168,85,247,.10);
  --text:#0d2d3d; --text-muted:#5a7a8a; --text-mutest:#8aa6b5;
  --b:rgba(0,115,153,.10); --b2:rgba(0,115,153,.20);
  --r:10px; --rL:14px; --tr:.18s ease;
  --ff-display:'Bebas Neue',sans-serif;
  --ff-body:'DM Sans',sans-serif;
  --ff-mono:'Space Mono',monospace;
  
  font-family:var(--ff-body);
  color:var(--text);
  background:var(--n1);
  width:100%;
  height:100%;
  display:flex;
  flex-direction:column;
  overflow:hidden;
  min-height:600px
}

/* HEADER */
.goc-hdr{
  display:flex;align-items:center;justify-content:space-between;
  padding:14px 22px;border-bottom:1.5px solid var(--n4);
  background:var(--n2);flex-shrink:0;gap:12px;flex-wrap:wrap
}
.goc-brand{display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0;flex:1}
.goc-logo{
  font-family:var(--ff-display);font-size:20px;letter-spacing:3px;
  background:linear-gradient(135deg,var(--primary),var(--primary-light));
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;
  background-clip:text;flex-shrink:0
}
.goc-oid{
  font-family:var(--ff-mono);font-size:10px;color:var(--text-muted);
  background:var(--n1);border:1px solid var(--n4);border-radius:6px;
  padding:3px 9px;letter-spacing:.5px;max-width:220px;overflow:hidden;
  text-overflow:ellipsis;white-space:nowrap
}
.goc-badge{
  display:inline-flex;align-items:center;gap:5px;padding:4px 11px;
  border-radius:20px;font-size:10px;font-weight:700;
  text-transform:uppercase;letter-spacing:.5px;border:1px solid;flex-shrink:0
}
.goc-badge.pending{background:var(--warn-soft);color:var(--warn);border-color:rgba(245,158,11,.25)}
.goc-badge.in_review{background:var(--teal-soft);color:var(--teal);border-color:rgba(79,172,254,.25)}
.goc-badge.draft_ready{background:var(--purple-soft);color:var(--purple);border-color:rgba(168,85,247,.25)}
.goc-badge.approved{background:var(--emerald-soft);color:var(--emerald);border-color:rgba(52,199,89,.25)}
.goc-badge.rejected{background:var(--err-soft);color:var(--err);border-color:rgba(239,68,68,.25)}
.goc-badge.completed{background:var(--gold-soft);color:var(--gold);border-color:rgba(201,162,39,.25)}

/* SOURCE BANNER */
.goc-source-banner{
  display:flex;align-items:center;gap:14px;padding:14px 22px;
  border-bottom:1.5px solid var(--n4);flex-shrink:0;flex-wrap:wrap
}
.goc-source-banner.custom{background:linear-gradient(135deg,var(--teal-soft),transparent)}
.goc-source-banner.template{background:linear-gradient(135deg,var(--emerald-soft),transparent)}
.goc-source-banner.ask{background:linear-gradient(135deg,var(--gold-soft),transparent)}
.goc-source-icon{
  font-size:22px;width:42px;height:42px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  flex-shrink:0;border:1.5px solid var(--n4);background:var(--n1)
}
.goc-source-banner.custom .goc-source-icon{color:var(--teal);border-color:rgba(79,172,254,.3)}
.goc-source-banner.template .goc-source-icon{color:var(--emerald);border-color:rgba(52,199,89,.3)}
.goc-source-banner.ask .goc-source-icon{color:var(--gold);border-color:rgba(201,162,39,.3)}
.goc-source-info{flex:1;min-width:200px}
.goc-source-title{font-size:14px;font-weight:700}
.goc-source-banner.custom .goc-source-title{color:var(--teal)}
.goc-source-banner.template .goc-source-title{color:var(--emerald)}
.goc-source-banner.ask .goc-source-title{color:var(--gold)}
.goc-source-sub{font-size:11px;color:var(--text-muted);margin-top:3px}
.goc-source-meta{font-size:10.5px;color:var(--text-mutest);margin-top:4px}
.goc-source-meta strong{color:var(--text)}
.goc-source-actions{display:flex;gap:6px;flex-shrink:0;flex-wrap:wrap}

/* ─── TABS ─── */
.goc-tabs{
  display:flex;border-bottom:1.5px solid var(--n4);
  background:var(--n2);flex-shrink:0;
  overflow-x:auto;scrollbar-width:none
}
.goc-tabs::-webkit-scrollbar{display:none}
.goc-tab{
  position:relative;padding:14px 22px;
  background:transparent;border:none;
  border-right:1px solid var(--n4);
  color:var(--text-muted);cursor:pointer;
  font-family:var(--ff-body);font-size:11.5px;
  font-weight:600;letter-spacing:.3px;
  display:flex;align-items:center;gap:8px;
  transition:all var(--tr);white-space:nowrap;
  flex:1;justify-content:center;min-width:140px
}
.goc-tab:last-child{border-right:none}
.goc-tab::after{
  content:'';position:absolute;bottom:-1.5px;
  left:0;right:0;height:3px;
  background:linear-gradient(90deg,var(--primary),var(--primary-light));
  transform:scaleX(0);transition:transform var(--tr)
}
.goc-tab.on{color:var(--primary);background:var(--n1);font-weight:700}
.goc-tab.on::after{transform:scaleX(1)}
.goc-tab:hover:not(.on){color:var(--text);background:var(--n3)}
.goc-tab-badge{
  font-size:9px;font-weight:700;padding:2px 8px;
  border-radius:20px;text-transform:uppercase;
  letter-spacing:.4px;border:1px solid
}
.goc-tab-badge.gold{background:var(--gold-soft);color:var(--gold);border-color:rgba(201,162,39,.3)}
.goc-tab-badge.teal{background:var(--teal-soft);color:var(--teal);border-color:rgba(79,172,254,.3)}
.goc-tab-badge.warn{background:var(--warn-soft);color:var(--warn);border-color:rgba(245,158,11,.3)}
.goc-tab-badge.ok{background:var(--emerald-soft);color:var(--emerald);border-color:rgba(52,199,89,.3)}
.goc-tab-badge.purple{
  background:var(--purple-soft);color:var(--purple);
  border-color:rgba(168,85,247,.3);
  animation:goc-pulse-soft 2s ease infinite
}
@keyframes goc-pulse-soft{
  0%,100%{box-shadow:0 0 0 0 rgba(168,85,247,.3)}
  50%{box-shadow:0 0 0 4px rgba(168,85,247,0)}
}
.goc-tab-badge.empty{background:var(--n3);color:var(--text-mutest);border-color:var(--n4)}

/* COUNTER STRIP */
.goc-counter-strip{
  display:flex;align-items:center;gap:10px;padding:10px 22px;
  border-bottom:1px solid var(--n4);background:var(--n2);
  flex-shrink:0;flex-wrap:wrap
}
.goc-counter-label{font-size:11px;color:var(--text-muted);font-weight:600}
.goc-counter-dots{display:flex;gap:6px;align-items:center}
.goc-counter-dot{
  width:11px;height:11px;border-radius:50%;flex-shrink:0;
  transition:all var(--tr)
}
.goc-counter-dot.used{background:var(--gold);box-shadow:0 0 0 2px rgba(201,162,39,.2)}
.goc-counter-dot.current{background:var(--teal);box-shadow:0 0 0 3px rgba(79,172,254,.3)}
.goc-counter-dot.empty{background:var(--n4);border:1.5px solid var(--n5)}
.goc-counter-text{font-size:11px;color:var(--text-muted);font-weight:600}
.goc-counter-text strong{color:var(--primary)}
.goc-counter-warn{
  margin-left:auto;font-size:10.5px;color:var(--warn);font-weight:600;
  display:inline-flex;align-items:center;gap:4px
}

/* ACTION STATUS */
.goc-action-status{
  padding:10px 22px;border-bottom:1px solid var(--n4);
  display:flex;align-items:center;gap:10px;font-size:12px;
  background:var(--n2);flex-shrink:0;flex-wrap:wrap
}
.goc-action-status.busy{background:var(--primary-softer);color:var(--primary)}
.goc-action-status.success{background:var(--emerald-soft);color:var(--emerald)}
.goc-action-status.error{background:var(--err-soft);color:var(--err)}
.goc-action-icon{
  display:inline-flex;align-items:center;justify-content:center;
  width:24px;height:24px;border-radius:50%;font-size:13px;
  flex-shrink:0;background:rgba(255,255,255,.4)
}

/* BODY */
.goc-body{
  flex:1;background:var(--n0);overflow-y:auto;
  display:flex;flex-direction:column;
  scrollbar-width:thin;scrollbar-color:var(--n5) transparent
}
.goc-body::-webkit-scrollbar{width:6px}
.goc-body::-webkit-scrollbar-thumb{background:var(--n5);border-radius:3px}

.goc-content{
  flex:1;padding:22px;display:flex;flex-direction:column;gap:18px
}

/* SECTIONS */
.goc-section{
  background:var(--n1);border-radius:var(--rL);
  border:1px solid var(--n4);padding:22px;
  box-shadow:0 2px 8px rgba(0,40,60,.03)
}
.goc-section-title{
  font-family:var(--ff-display);font-size:20px;
  letter-spacing:2px;color:var(--primary);
  margin-bottom:6px;display:flex;align-items:center;gap:10px
}
.goc-section-title::before{
  content:'';width:5px;height:20px;
  background:linear-gradient(180deg,var(--primary),var(--primary-light));
  border-radius:3px
}
.goc-section-sub{
  font-size:12px;color:var(--text-muted);
  margin-bottom:14px;line-height:1.5
}

/* ─── REQUEST CARD ─── */
.goc-request-cards{display:flex;flex-direction:column;gap:10px}

.goc-request-card{
  background:var(--n2);border:1.5px solid var(--n4);
  border-radius:var(--rL);padding:14px 16px;
  cursor:pointer;transition:all var(--tr)
}
.goc-request-card:hover{
  border-color:var(--primary-light);background:var(--n3);
  transform:translateY(-1px);
  box-shadow:0 4px 12px rgba(0,115,153,.1)
}
.goc-request-card.expanded{
  background:var(--n1);border-color:var(--primary);
  box-shadow:0 6px 18px rgba(0,115,153,.1)
}

.goc-request-card-header{
  display:flex;align-items:flex-start;gap:12px;width:100%
}

.goc-request-num{
  width:36px;height:36px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  font-family:var(--ff-display);font-size:18px;
  background:linear-gradient(135deg,var(--gold),#f0d060);
  color:#fff;flex-shrink:0;
  box-shadow:0 4px 10px rgba(201,162,39,.3)
}
.goc-request-num.revision{
  background:linear-gradient(135deg,var(--warn),#fbbf24);
  box-shadow:0 4px 10px rgba(245,158,11,.3)
}

.goc-request-info{flex:1;min-width:0}
.goc-request-head{
  display:flex;align-items:center;gap:10px;
  margin-bottom:6px;flex-wrap:wrap
}
.goc-request-label{
  font-size:13px;font-weight:700;color:var(--primary);letter-spacing:.3px
}
.goc-request-tag{
  display:inline-flex;align-items:center;gap:4px;
  padding:2px 8px;border-radius:12px;
  font-size:9px;font-weight:700;text-transform:uppercase;
  letter-spacing:.4px;border:1px solid
}
.goc-request-tag.original{background:var(--gold-soft);color:var(--gold);border-color:rgba(201,162,39,.25)}
.goc-request-tag.revision{background:var(--warn-soft);color:var(--warn);border-color:rgba(245,158,11,.25)}
.goc-request-date{font-size:11px;color:var(--text-muted);font-family:var(--ff-mono)}
.goc-request-summary{
  font-size:12px;color:var(--text-muted);line-height:1.5;
  overflow:hidden;text-overflow:ellipsis;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical
}
.goc-request-meta-pills{display:flex;gap:5px;margin-top:8px;flex-wrap:wrap}
.goc-request-pill{
  padding:2px 9px;border-radius:12px;
  background:var(--primary-soft);color:var(--primary);
  font-size:10px;font-weight:600;border:1px solid var(--b)
}

.goc-request-expand-icon{
  width:28px;height:28px;border-radius:50%;
  background:var(--n3);display:flex;align-items:center;justify-content:center;
  font-size:14px;color:var(--text-muted);
  flex-shrink:0;transition:transform var(--tr)
}
.goc-request-card.expanded .goc-request-expand-icon{
  transform:rotate(180deg);background:var(--primary);color:#fff
}

.goc-request-detail{
  padding:16px;background:var(--n2);border-radius:10px;
  margin-top:12px;border:1px solid var(--n4)
}
.goc-detail-grid{
  display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px
}
.goc-detail-cell{
  padding:9px 12px;background:var(--n1);
  border:1px solid var(--n4);border-radius:8px;
  display:flex;flex-direction:column;gap:3px
}
.goc-detail-k{
  font-size:9px;color:var(--text-muted);text-transform:uppercase;
  letter-spacing:.6px;font-weight:700
}
.goc-detail-v{font-size:12.5px;color:var(--text);font-weight:600;word-break:break-word}
.goc-detail-field{margin-bottom:10px}
.goc-detail-field-label{
  font-size:10px;font-weight:700;color:var(--text-muted);
  text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px
}
.goc-detail-field-label.avoid{color:var(--err)}
.goc-detail-field-label.required{color:var(--emerald)}
.goc-detail-field-value{
  padding:9px 12px;background:var(--n1);
  border:1px solid var(--n4);border-radius:8px;
  font-size:12.5px;color:var(--text);line-height:1.5;
  white-space:pre-wrap;word-break:break-word
}
.goc-detail-field-value.avoid{background:var(--err-soft);border-color:rgba(239,68,68,.2)}
.goc-detail-field-value.required{background:var(--emerald-soft);border-color:rgba(52,199,89,.2)}
.goc-detail-chips{display:flex;flex-wrap:wrap;gap:5px}
.goc-detail-chip{
  padding:4px 10px;border-radius:14px;
  background:var(--primary-soft);color:var(--primary);
  border:1px solid var(--b2);font-size:11px;font-weight:600
}
.goc-detail-chip.style{background:var(--teal-soft);color:var(--teal);border-color:rgba(79,172,254,.25)}
.goc-detail-refs{
  display:grid;grid-template-columns:repeat(auto-fill,minmax(90px,1fr));
  gap:8px;margin-top:6px
}
.goc-detail-refs-header{
  display:flex;align-items:center;justify-content:space-between;
  margin-bottom:6px
}
.goc-detail-ref{
  position:relative;aspect-ratio:1;border-radius:8px;
  overflow:hidden;background:var(--n3);border:1.5px solid var(--n4)
}
.goc-detail-ref img{width:100%;height:100%;object-fit:cover;display:block}
.goc-detail-ref-num{
  position:absolute;top:4px;left:4px;
  background:rgba(0,40,60,.7);color:#fff;
  padding:2px 7px;border-radius:6px;font-size:9px;font-weight:700
}
.goc-detail-ref-dl{
  position:absolute;bottom:4px;right:4px;
  background:rgba(0,40,60,.75);color:#fff;
  border:none;border-radius:6px;
  width:24px;height:24px;
  display:flex;align-items:center;justify-content:center;
  font-size:12px;cursor:pointer;
  opacity:0;transition:opacity .18s;
  backdrop-filter:blur(3px)
}
.goc-detail-ref:hover .goc-detail-ref-dl{opacity:1}
.goc-dl-all-btn{
  display:flex;align-items:center;gap:5px;
  background:var(--primary-soft);color:var(--primary);
  border:1px solid var(--b2);border-radius:8px;
  padding:4px 10px;font-size:11px;font-weight:700;
  cursor:pointer;transition:all .18s
}
.goc-dl-all-btn:hover{background:var(--primary);color:#fff}

/* ─── DESIGN CARD (seller) ─── */
.goc-design-cards{display:flex;flex-direction:column;gap:14px}

.goc-design-card{
  background:linear-gradient(135deg,var(--n1),var(--purple-soft));
  border:2px solid rgba(168,85,247,.25);
  border-radius:var(--rL);padding:18px;
  transition:all var(--tr)
}
.goc-design-card.latest{
  border-color:rgba(168,85,247,.45);
  box-shadow:0 6px 20px rgba(168,85,247,.15)
}
.goc-design-card.action-required{
  border-color:var(--purple);
  box-shadow:0 8px 24px rgba(168,85,247,.25);
  animation:goc-glow 2.5s ease infinite
}
.goc-design-card.action-required{
  border-color:var(--purple);
  animation:goc-glow 2.5s ease infinite
}
@keyframes goc-glow{
  0%,100%{box-shadow:0 8px 24px rgba(168,85,247,.25)}
  50%{box-shadow:0 8px 32px rgba(168,85,247,.4)}
}

.goc-design-head{
  display:flex;align-items:center;gap:14px;
  padding-bottom:14px;border-bottom:1px solid var(--n4);
  margin-bottom:14px;flex-wrap:wrap
}
.goc-design-icon{
  width:50px;height:50px;border-radius:50%;
  background:var(--n1);
  display:flex;align-items:center;justify-content:center;
  font-size:24px;border:2px solid rgba(168,85,247,.3);
  flex-shrink:0
}
.goc-design-info{flex:1;min-width:180px}
.goc-design-title{
  font-family:var(--ff-display);font-size:20px;
  letter-spacing:1px;color:var(--purple);margin-bottom:3px
}
.goc-design-sub{font-size:11px;color:var(--text-muted)}
.goc-design-tag{
  display:inline-flex;align-items:center;gap:4px;
  padding:3px 10px;border-radius:14px;
  font-size:10px;font-weight:700;text-transform:uppercase;
  letter-spacing:.4px;border:1px solid;flex-shrink:0
}
.goc-design-tag.latest{
  background:var(--purple-soft);color:var(--purple);
  border-color:rgba(168,85,247,.3)
}
.goc-design-tag.action{
  background:var(--purple);color:#fff;
  border-color:var(--purple);
  animation:goc-pulse-soft 1.5s ease infinite
}
.goc-design-tag.approved{
  background:var(--emerald-soft);color:var(--emerald);
  border-color:rgba(52,199,89,.3)
}
.goc-design-tag.rejected{
  background:var(--err-soft);color:var(--err);
  border-color:rgba(239,68,68,.3)
}
.goc-design-tag.history{
  background:var(--n2);color:var(--text-muted);
  border-color:var(--n4)
}

.goc-design-stats{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  gap:8px;margin-bottom:14px
}
.goc-design-stat{
  padding:10px 12px;background:var(--n1);
  border:1px solid var(--n4);border-radius:8px;
  text-align:center
}
.goc-design-stat-v{
  font-family:var(--ff-display);font-size:22px;
  color:var(--primary);line-height:1
}
.goc-design-stat-l{
  font-size:9px;color:var(--text-muted);
  text-transform:uppercase;letter-spacing:.5px;
  margin-top:4px;font-weight:600
}

.goc-design-notes{
  padding:11px 14px;background:var(--purple-soft);
  border:1px solid rgba(168,85,247,.2);
  border-radius:8px;font-size:12px;color:var(--text);
  line-height:1.5;white-space:pre-wrap;margin-bottom:14px
}
.goc-design-notes-label{
  font-size:10px;color:var(--purple);
  text-transform:uppercase;letter-spacing:.6px;
  font-weight:700;margin-bottom:5px
}

.goc-design-actions{
  display:flex;gap:8px;flex-wrap:wrap;
  padding-top:14px;border-top:1px solid var(--n4)
}
.goc-design-actions .goc-btn{flex:1;min-width:110px}

/* ─── ASK FORM ─── */
.goc-form-section{
  background:var(--n1);border-radius:var(--rL);
  border:2px dashed var(--primary-light);padding:22px;
  position:relative
}
.goc-form-section::before{
  content:'';position:absolute;inset:0;
  background:linear-gradient(135deg,var(--primary-softer),transparent);
  border-radius:var(--rL);pointer-events:none
}
.goc-form-title{
  font-family:var(--ff-display);font-size:22px;
  letter-spacing:2px;color:var(--primary);
  margin-bottom:6px;
  display:flex;align-items:center;gap:10px;position:relative
}
.goc-form-title::before{
  content:'';width:5px;height:22px;
  background:linear-gradient(180deg,var(--primary),var(--primary-light));
  border-radius:3px
}
.goc-form-sub{
  font-size:12px;color:var(--text-muted);
  margin-bottom:18px;line-height:1.5;position:relative
}
.goc-form-grid{
  display:grid;grid-template-columns:1fr 1fr;gap:18px;position:relative
}
.goc-form-col{display:flex;flex-direction:column;gap:14px}
.goc-form-field{display:flex;flex-direction:column;gap:6px}
.goc-form-label{
  font-size:11px;font-weight:700;color:var(--text-muted);
  text-transform:uppercase;letter-spacing:.6px;
  display:flex;align-items:center;gap:5px
}
.goc-form-label.required::after{content:'*';color:var(--err);font-weight:700}
.goc-form-label.avoid{color:var(--err)}
.goc-form-label.ok{color:var(--emerald)}
.goc-form-input,.goc-form-select,.goc-form-textarea{
  background:var(--n1);border:1.5px solid var(--n4);
  border-radius:8px;color:var(--text);
  font-family:var(--ff-body);font-size:13px;
  padding:9px 12px;width:100%;outline:none;
  transition:border-color var(--tr)
}
.goc-form-input:focus,.goc-form-select:focus,.goc-form-textarea:focus{
  border-color:var(--primary);box-shadow:0 0 0 3px var(--primary-softer)
}
.goc-form-textarea{resize:vertical;min-height:70px}
.goc-form-textarea.avoid{border-color:rgba(239,68,68,.3)}
.goc-form-textarea.avoid:focus{border-color:var(--err);box-shadow:0 0 0 3px var(--err-soft)}
.goc-form-textarea.ok{border-color:rgba(52,199,89,.3)}
.goc-form-textarea.ok:focus{border-color:var(--emerald);box-shadow:0 0 0 3px var(--emerald-soft)}
.goc-form-row-2{display:grid;grid-template-columns:1fr 1fr;gap:10px}

.goc-chips-wrap{display:flex;flex-wrap:wrap;gap:5px}
.goc-chip{
  padding:5px 11px;border-radius:14px;
  border:1.5px solid var(--n4);background:var(--n2);
  color:var(--text-muted);font-size:11px;font-weight:600;
  cursor:pointer;transition:all var(--tr)
}
.goc-chip:hover{border-color:var(--primary);color:var(--text)}
.goc-chip.on{border-color:var(--primary);background:var(--primary-soft);color:var(--primary)}

.goc-toggle-row{display:flex;flex-wrap:wrap;gap:5px}
.goc-toggle-btn{
  padding:6px 12px;border-radius:8px;
  background:var(--n2);border:1.5px solid var(--n4);
  color:var(--text-muted);font-size:11px;font-weight:600;
  cursor:pointer;transition:all var(--tr)
}
.goc-toggle-btn:hover{border-color:var(--teal);color:var(--text)}
.goc-toggle-btn.active{background:var(--teal-soft);border-color:var(--teal);color:var(--teal)}

.goc-insp-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.goc-insp-card{
  display:flex;align-items:center;gap:10px;padding:10px 12px;
  border:1.5px solid var(--n4);background:var(--n2);
  border-radius:10px;cursor:pointer;transition:all var(--tr)
}
.goc-insp-card:hover{border-color:var(--primary);background:var(--n3)}
.goc-insp-card.on{border-color:var(--primary);background:var(--primary-soft)}
.goc-insp-card-icon{font-size:22px;flex-shrink:0}
.goc-insp-card-text{flex:1;min-width:0}
.goc-insp-card-title{font-size:12px;font-weight:700;color:var(--text);margin-bottom:2px}
.goc-insp-card.on .goc-insp-card-title{color:var(--primary)}
.goc-insp-card-desc{font-size:10px;color:var(--text-muted)}
.goc-insp-card-check{color:var(--primary);font-size:14px;font-weight:700}

.goc-refs-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.goc-ref-slot{
  position:relative;aspect-ratio:1;border-radius:10px;
  background:var(--n2);border:1.5px dashed var(--n5);
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  cursor:pointer;gap:5px;transition:all var(--tr);overflow:hidden
}
.goc-ref-slot:hover{border-color:var(--primary);background:var(--primary-softer)}
.goc-ref-slot.has{border-style:solid;border-color:var(--n4)}
.goc-ref-slot.has img{width:100%;height:100%;object-fit:cover;position:absolute;inset:0}
.goc-ref-slot-empty{display:flex;flex-direction:column;align-items:center;gap:5px}
.goc-ref-slot-plus{font-size:28px;color:var(--text-mutest)}
.goc-ref-slot-label{font-size:10px;color:var(--text-muted)}
.goc-ref-remove{
  position:absolute;top:5px;right:5px;
  width:22px;height:22px;background:var(--err);
  border:none;border-radius:50%;color:#fff;
  font-size:11px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  z-index:2;box-shadow:0 2px 6px rgba(0,0,0,.2)
}

.goc-form-actions{
  display:flex;gap:10px;margin-top:18px;
  padding-top:16px;border-top:1px solid var(--n4);
  flex-wrap:wrap;justify-content:flex-end;position:relative
}

.goc-form-warn{
  padding:10px 14px;border-radius:8px;
  background:var(--warn-soft);
  border:1px solid rgba(245,158,11,.25);
  color:var(--warn);font-size:11.5px;line-height:1.5;
  font-weight:600;display:flex;align-items:flex-start;gap:8px
}
.goc-form-info{
  padding:10px 14px;border-radius:8px;
  background:var(--teal-soft);
  border:1px solid rgba(79,172,254,.25);
  color:var(--teal);font-size:11.5px;line-height:1.5;
  font-weight:600;display:flex;align-items:flex-start;gap:8px
}

.goc-limit-reached{
  background:var(--warn-soft);
  border:1.5px solid rgba(245,158,11,.3);
  border-radius:var(--rL);padding:22px;
  text-align:center;display:flex;flex-direction:column;
  align-items:center;gap:12px
}
.goc-limit-icon{font-size:42px}
.goc-limit-title{
  font-family:var(--ff-display);font-size:22px;
  letter-spacing:1px;color:var(--warn)
}
.goc-limit-sub{
  font-size:12px;color:var(--text-muted);
  max-width:420px;line-height:1.5
}

/* CTA BANNERS */
.goc-cta-banner{
  text-align:center;padding:24px;
  background:var(--n1);border-radius:var(--rL);
  border:1px solid var(--n4)
}
.goc-cta-banner-icon{font-size:50px;opacity:.2;margin-bottom:10px}
.goc-cta-banner-title{
  font-family:var(--ff-display);font-size:22px;
  color:var(--primary);letter-spacing:1.5px;margin-bottom:8px
}
.goc-cta-banner-sub{
  font-size:13px;color:var(--text-muted);
  max-width:380px;margin:0 auto 18px;line-height:1.5
}

.goc-revision-cta{
  text-align:center;padding:18px;
  background:var(--n2);border-radius:var(--rL);
  border:1.5px dashed var(--n5)
}
.goc-revision-cta-text{
  font-size:12px;color:var(--text-muted);margin-bottom:12px
}

/* BUTTONS */
.goc-btn{
  display:inline-flex;align-items:center;justify-content:center;gap:6px;
  padding:9px 18px;border-radius:8px;font-family:var(--ff-body);
  font-size:12px;font-weight:600;cursor:pointer;border:none;
  transition:all var(--tr);letter-spacing:.4px;text-transform:uppercase;
  white-space:nowrap
}
.goc-btn:disabled{opacity:.45;cursor:not-allowed;pointer-events:none}
.goc-btn-primary{
  background:linear-gradient(135deg,var(--primary),var(--primary-light));
  color:#fff;box-shadow:0 4px 14px rgba(0,115,153,.28)
}
.goc-btn-primary:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.goc-btn-teal{
  background:linear-gradient(135deg,var(--teal),var(--teal-light));
  color:#fff;box-shadow:0 4px 14px rgba(79,172,254,.28)
}
.goc-btn-teal:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.goc-btn-success{
  background:linear-gradient(135deg,var(--emerald),#4ade80);
  color:#fff;box-shadow:0 4px 14px rgba(52,199,89,.28)
}
.goc-btn-success:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.goc-btn-warn{
  background:linear-gradient(135deg,var(--warn),#fbbf24);
  color:#fff;box-shadow:0 4px 14px rgba(245,158,11,.28)
}
.goc-btn-warn:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.goc-btn-danger{
  background:linear-gradient(135deg,var(--err),#f87171);
  color:#fff;box-shadow:0 4px 14px rgba(239,68,68,.28)
}
.goc-btn-danger:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.goc-btn-purple{
  background:linear-gradient(135deg,var(--purple),#c084fc);
  color:#fff;box-shadow:0 4px 14px rgba(168,85,247,.28)
}
.goc-btn-purple:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.goc-btn-gold{
  background:linear-gradient(135deg,var(--gold),#f0d060);
  color:#fff;box-shadow:0 4px 14px rgba(201,162,39,.28)
}
.goc-btn-gold:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.goc-btn-secondary{
  background:var(--n2);color:var(--text);border:1.5px solid var(--n4)
}
.goc-btn-secondary:hover:not(:disabled){background:var(--n3);border-color:var(--primary)}
.goc-btn-ghost{
  background:transparent;color:var(--text-muted);border:1.5px solid var(--n4)
}
.goc-btn-ghost:hover:not(:disabled){
  color:var(--primary);border-color:var(--primary);background:var(--primary-softer)
}
.goc-btn-sm{padding:6px 12px;font-size:10.5px}
.goc-btn-close{
  width:36px;height:36px;padding:0;border-radius:50%;
  background:var(--n2);border:1.5px solid var(--n4);
  color:var(--text-muted);font-size:14px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  transition:all var(--tr);flex-shrink:0
}
.goc-btn-close:hover{background:var(--err);border-color:var(--err);color:#fff;transform:rotate(90deg)}

/* EMPTY STATE */
.goc-empty-state{
  flex:1;display:flex;flex-direction:column;
  align-items:center;justify-content:center;
  gap:18px;padding:60px 30px;text-align:center
}
.goc-empty-icon{font-size:72px;opacity:.18}
.goc-empty-title{
  font-family:var(--ff-display);font-size:30px;
  letter-spacing:2px;color:var(--primary)
}
.goc-empty-sub{
  font-size:13px;color:var(--text-muted);
  max-width:380px;line-height:1.6
}

/* FOOTER */
.goc-footer{
  display:flex;align-items:center;justify-content:space-between;
  padding:14px 22px;border-top:1.5px solid var(--n4);
  background:var(--n1);gap:14px;flex-wrap:wrap;flex-shrink:0
}
.goc-footer-l{display:flex;align-items:center;gap:14px;flex-wrap:wrap}
.goc-footer-r{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.goc-price-label{
  font-size:10px;color:var(--text-muted);
  text-transform:uppercase;letter-spacing:1px;
  font-weight:600;display:block;margin-bottom:2px
}
.goc-price-value{
  font-family:var(--ff-display);font-size:24px;font-weight:700;
  background:linear-gradient(135deg,var(--primary),var(--primary-light));
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;
  background-clip:text;letter-spacing:1px;line-height:1
}

@media(max-width:900px){
  .goc-form-grid{grid-template-columns:1fr}
  .goc-detail-grid{grid-template-columns:1fr}
  .goc-footer{flex-direction:column;align-items:stretch}
  .goc-footer-l,.goc-footer-r{justify-content:center}
  .goc-design-stats{grid-template-columns:repeat(3,1fr)}
}
@media(max-width:600px){
  .goc-hdr,.goc-source-banner,.goc-counter-strip,
  .goc-action-status,.goc-footer{padding:12px 14px}
  .goc-content{padding:14px}
  .goc-form-row-2,.goc-insp-grid,.goc-refs-grid{grid-template-columns:1fr}
  .goc-price-value{font-size:20px}
  .goc-empty-title{font-size:24px}
  .goc-section,.goc-form-section,.goc-design-card{padding:16px}
  .goc-section-title,.goc-form-title{font-size:18px}
  .goc-design-actions .goc-btn{min-width:auto}
  .goc-tab{padding:12px 14px;min-width:120px}
}
`;

/* ══════════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════════ */

const extractProduct = (order) => {
  if (!order) return null;
  const p = order.products;
  if (!p) return null;
  if (Array.isArray(p)) return p[0] || null;
  if (typeof p === "object") return p;
  return null;
};

const getRefSrc = (ref) => {
  if (!ref) return null;
  if (typeof File !== "undefined" && ref instanceof File) return URL.createObjectURL(ref);
  if (typeof ref === "string" && ref.length > 0) return ref;
  if (typeof ref === "object" && ref.urlImg) return ref.urlImg;
  return null;
};

const downloadImage = async (src, filename = "reference.png") => {
  try {
    const dlUrl = src.includes("ask-ref-image")
      ? src + (src.includes("?") ? "&download=1" : "?download=1")
      : src;
    const res = await fetch(dlUrl);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 300);
  } catch (err) {
    console.error("[downloadImage] Failed:", err);
  }
};

const downloadAllImages = async (images, askIndex = 0) => {
  const srcs = images.map(r => getRefSrc(r)).filter(Boolean);
  for (let i = 0; i < srcs.length; i++) {
    await downloadImage(srcs[i], `ask${askIndex + 1}_ref${i + 1}.png`);
    if (i < srcs.length - 1) await new Promise(r => setTimeout(r, 400));
  }
};

const formatDate = (date) => {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric"
    });
  } catch { return String(date); }
};

const formatSubmittedShort = (date) => {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch { return String(date); }
};

const formatSourceTypeLabel = (sourceType) => {
  switch (sourceType) {
    case "custom":   return { icon: "🎨", label: __elakayText("t040", "Your Custom Design"), sub: elakayAutoText("graffiti_client_source_custom_help", "Design you created yourself") };
    case "ask":      return { icon: "💬", label: __elakayText("t041", "Custom Request to Artist"), sub: elakayAutoText("graffiti_client_source_ask_help", "You asked the artist to design something for you") };
    case "template": return { icon: "📋", label: __elakayText("t042", "Selected Template"), sub: elakayAutoText("graffiti_client_source_template_help", "Template you customized") };
    default:         return { icon: "❓", label: __elakayText("t091", "Unknown"), sub: "" };
  }
};

const formatStatusLabel = (status) => {
  switch (status) {
    case "pending":     return elakayAutoText("graffiti_status_pending", "⏳ Pending");
    case "in_review":   return elakayAutoText("graffiti_status_in_review", "🔍 In Review");
    case "draft_ready": return elakayAutoText("graffiti_status_design_ready", "📝 Design Ready");
    case "approved":    return elakayAutoText("graffiti_status_approved", "✓ Approved");
    case "rejected":    return elakayAutoText("graffiti_status_rejected", "✕ Rejected");
    case "completed":   return elakayAutoText("graffiti_status_completed", "✅ Completed");
    default:            return status || "—";
  }
};

const initialFormState = () => ({
  specialInstructions: "",
  artistMessage: "",
  avoidInstructions: "",
  requiredInstructions: "",
  desiredDate: "",
  budget: "",
  detailLevel: "Modéré",
  stylePrefs: [],
  inspirations: [],
  images: [null, null, null, null]
});

/* ══════════════════════════════════════════════════════
   ASK REQUEST CARD
══════════════════════════════════════════════════════ */
function AskRequestCard({ askRequest, index, expanded, onToggle, infoSource }) {
  __setElakayInfoSource(infoSource);
  const text = (key, fallback) => getHardcodedText(infoSource, "graffitiOrderClient", key, fallback);

  const ask = askRequest || {};
  const isRevision = index > 0;
  const summary = ask.specialInstructions || ask.artistMessage ||
                  ask.requiredInstructions || "No instructions provided";

  const pills = [];
  if (ask.detailLevel) pills.push(ask.detailLevel);
  if (ask.budget) pills.push(`Budget: ${ask.budget}`);
  if (ask.stylePrefs?.length > 0) pills.push(`${ask.stylePrefs.length} style${ask.stylePrefs.length > 1 ? "s" : ""}`);
  const refsWithSrc = ask.images?.filter(r => getRefSrc(r)) || [];
  if (refsWithSrc.length > 0) pills.push(`${refsWithSrc.length} ref${refsWithSrc.length > 1 ? "s" : ""}`);

  return (
    <div className={`goc-request-card${expanded ? " expanded" : ""}`}>
      <div className="goc-request-card-header" onClick={onToggle}>
        <div className={`goc-request-num${isRevision ? " revision" : ""}`}>
          {index + 1}
        </div>
        <div className="goc-request-info">
          <div className="goc-request-head">
            <div className="goc-request-label">{__elakayText("t043", "Request #")}{index + 1}</div>
            <span className={`goc-request-tag ${isRevision ? "revision" : "original"}`}>
              {isRevision ? elakayAutoText("h_01e906e8666908", "✎ Revision") : elakayAutoText("h_c0a8060f3b1102", "Original")}
            </span>
            <div className="goc-request-date">{formatSubmittedShort(ask.submittedAt)}</div>
          </div>
          <div className="goc-request-summary">{summary}</div>
          {pills.length > 0 && (
            <div className="goc-request-meta-pills">
              {pills.map((p, i) => (
                <span key={i} className="goc-request-pill">{p}</span>
              ))}
            </div>
          )}
        </div>
        <div className="goc-request-expand-icon">▼</div>
      </div>

      {expanded && (
        <div className="goc-request-detail">
          <div className="goc-detail-grid">
            <div className="goc-detail-cell">
              <span className="goc-detail-k">{text("t001", "Budget")}</span>
              <span className="goc-detail-v">{ask.budget || "—"}</span>
            </div>
            <div className="goc-detail-cell">
              <span className="goc-detail-k">{text("t002", "Detail Level")}</span>
              <span className="goc-detail-v">{ask.detailLevel || "—"}</span>
            </div>
            <div className="goc-detail-cell">
              <span className="goc-detail-k">{text("t003", "Desired Date")}</span>
              <span className="goc-detail-v">{formatDate(ask.desiredDate)}</span>
            </div>
            <div className="goc-detail-cell">
              <span className="goc-detail-k">{text("t004", "Submitted")}</span>
              <span className="goc-detail-v">{formatSubmittedShort(ask.submittedAt)}</span>
            </div>
          </div>

          {ask.inspirations?.length > 0 && (
            <div className="goc-detail-field">
              <div className="goc-detail-field-label">💡 {text("t005", "Style Inspirations")}</div>
              <div className="goc-detail-chips">
                {ask.inspirations.map((insp, i) => (
                  <span key={i} className="goc-detail-chip">{insp}</span>
                ))}
              </div>
            </div>
          )}

          {ask.stylePrefs?.length > 0 && (
            <div className="goc-detail-field">
              <div className="goc-detail-field-label">🎨 {text("t006", "Artistic Styles")}</div>
              <div className="goc-detail-chips">
                {ask.stylePrefs.map((s, i) => (
                  <span key={i} className="goc-detail-chip style">{s}</span>
                ))}
              </div>
            </div>
          )}

          {ask.specialInstructions && (
            <div className="goc-detail-field">
              <div className="goc-detail-field-label">📝 {text("t007", "General Instructions")}</div>
              <div className="goc-detail-field-value">{ask.specialInstructions}</div>
            </div>
          )}

          {ask.artistMessage && (
            <div className="goc-detail-field">
              <div className="goc-detail-field-label">💬 {text("t008", "Message to Artist")}</div>
              <div className="goc-detail-field-value">{ask.artistMessage}</div>
            </div>
          )}

          {ask.avoidInstructions && (
            <div className="goc-detail-field">
              <div className="goc-detail-field-label avoid">❌ {text("t009", "Things to Avoid")}</div>
              <div className="goc-detail-field-value avoid">{ask.avoidInstructions}</div>
            </div>
          )}

          {ask.requiredInstructions && (
            <div className="goc-detail-field">
              <div className="goc-detail-field-label required">✅ {text("t010", "Required Elements")}</div>
              <div className="goc-detail-field-value required">{ask.requiredInstructions}</div>
            </div>
          )}

          {ask.images?.some(r => getRefSrc(r)) && (
            <div className="goc-detail-field">
              <div className="goc-detail-refs-header">
                <div className="goc-detail-field-label">🖼️ {text("t011", "Reference Images")}</div>
                {ask.images.filter(r => getRefSrc(r)).length > 1 && (
                  <button type="button" className="goc-dl-all-btn"
                    onClick={() => downloadAllImages(ask.images, index)}>
                    ⬇ {__elakayText("t099", "Download All")}
                  </button>
                )}
              </div>
              <div className="goc-detail-refs">
                {ask.images.map((ref, i) => {
                  const src = getRefSrc(ref);
                  if (!src) return null;
                  return (
                    <div key={i} className="goc-detail-ref">
                      <img src={src} alt={elakayAutoText("h_5e13f5795099fc", "Reference {value0}", { value0: i + 1 })}
                        crossOrigin="anonymous"
                        onError={e => { e.target.style.display = "none"; }} />
                      <span className="goc-detail-ref-num">{i + 1}</span>
                      <button type="button" className="goc-detail-ref-dl"
                        title={elakayAutoText("h_83d2a36b956934", "Download ref {value0}", { value0: i + 1 })}
                        onClick={() => downloadImage(src, `ask${index + 1}_ref${i + 1}.png`)}>
                        ⬇
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   SELLER DESIGN CARD
══════════════════════════════════════════════════════ */
function SellerDesignCard({ designGroup, index, isLatest, isPending, onView, onApprove, onReject, busy, busyMode, infoSource }) {
  const text = (key, fallback) => getHardcodedText(infoSource, "graffitiOrderClient", key, fallback);

  const stats = useMemo(() => {
    let textsCount = 0;
    let layersCount = 0;
    designGroup.slots.forEach(s => {
      textsCount += s.slotState?.texts?.length || 0;
      layersCount += s.slotState?.layers?.length || 0;
    });
    return {
      slots: designGroup.slots.length,
      texts: textsCount,
      layers: layersCount
    };
  }, [designGroup]);

  /* Statut visuel de cette soumission */
  const cardStatus = (() => {
    if (isLatest && isPending) return "action-required";
    if (isLatest) return "latest";
    return "history";
  })();

  const tagInfo = (() => {
    if (isLatest && isPending) {
      return { className: "action", text: __elakayText("t044", "⚡ Action Required") };
    }
    if (designGroup.graphicStatus === "approved") {
      return { className: "approved", text: __elakayText("t045", "✓ Approved") };
    }
    if (designGroup.graphicStatus === "rejected") {
      return { className: "rejected", text: __elakayText("t046", "✕ Rejected") };
    }
    if (isLatest) {
      return { className: "latest", text: __elakayText("t092", "Latest") };
    }
    return { className: "history", text: __elakayText("t047", "Previous Version") };
  })();

  return (
    <div className={`goc-design-card ${cardStatus}`}>

      <div className="goc-design-head">
        <div className="goc-design-icon">🎨</div>
        <div className="goc-design-info">
          <div className="goc-design-title">{__elakayText("t048", "Design Submission #")}{index + 1}
          </div>
          <div className="goc-design-sub">{__elakayText("t004", "Submitted")}{formatSubmittedShort(designGroup.submittedAt)}
          </div>
        </div>
        <span className={`goc-design-tag ${tagInfo.className}`}>
          {tagInfo.text}
        </span>
      </div>

      <div className="goc-design-stats">
        <div className="goc-design-stat">
          <div className="goc-design-stat-v">{stats.slots}</div>
          <div className="goc-design-stat-l">{__elakayText("t093", "Face")}{stats.slots > 1 ? "s" : ""}</div>
        </div>
        <div className="goc-design-stat">
          <div className="goc-design-stat-v">{stats.texts}</div>
          <div className="goc-design-stat-l">{__elakayText("t094", "Text")}{stats.texts > 1 ? "s" : ""}</div>
        </div>
        <div className="goc-design-stat">
          <div className="goc-design-stat-v">{stats.layers}</div>
          <div className="goc-design-stat-l">{__elakayText("t095", "Layer")}{stats.layers > 1 ? "s" : ""}</div>
        </div>
      </div>

      {designGroup.notes && (
        <div className="goc-design-notes">
          <div className="goc-design-notes-label">💼 {text("t012", "Notes from the artist")}</div>
          {designGroup.notes}
        </div>
      )}

      <div className="goc-design-actions">
        <button type="button" className="goc-btn goc-btn-purple"
          onClick={() => onView(designGroup)}>{__elakayText("t049", "👁️ View Design")}</button>

        {/* Actions Approve/Reject uniquement sur la dernière soumission en pending */}
        {isLatest && isPending && (
          <>
            <button type="button" className="goc-btn goc-btn-success"
              onClick={onApprove}
              disabled={busy}>
              {busy && busyMode === "approve" ? "⏳..." : elakayAutoText("h_6e9eb370207302", "✓ Approve")}
            </button>
            <button type="button" className="goc-btn goc-btn-danger"
              onClick={onReject}
              disabled={busy}>
              {busy && busyMode === "reject" ? "⏳..." : elakayAutoText("h_07e1a559156fdf", "✕ Reject")}
            </button>
          </>
        )}
      </div>

    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ASK FORM
══════════════════════════════════════════════════════ */
function AskForm({ requestNumber, onSubmit, onCancel, busy, hasExistingRequests, infoSource }) {
  const text = (key, fallback) => getHardcodedText(infoSource, "graffitiOrderClient", key, fallback);

  const [form, setForm] = useState(initialFormState);
  const fileRef = useRef(null);
  const [activeRefIndex, setActiveRefIndex] = useState(null);

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleStyle = (s) => {
    setF("stylePrefs", form.stylePrefs.includes(s)
      ? form.stylePrefs.filter(x => x !== s)
      : [...form.stylePrefs, s]);
  };

  const toggleInspiration = (s) => {
    setF("inspirations", form.inspirations.includes(s)
      ? form.inspirations.filter(x => x !== s)
      : [...form.inspirations, s]);
  };

  const handleFileSelect = (e) => {
    if (activeRefIndex === null || !e.target.files[0]) return;
    const newRefs = form.images.map((r, i) => i === activeRefIndex ? e.target.files[0] : r);
    setF("images", newRefs);
    e.target.value = "";
  };

  const handleRemoveRef = (i) => {
    setF("images", form.images.map((r, idx) => idx === i ? null : r));
  };

  const isFormValid = form.specialInstructions.trim().length > 0;

  const handleSubmit = () => {
    if (!isFormValid) return;
    onSubmit(form);
  };

  return (
    <div className="goc-form-section">
      <div className="goc-form-title">
        ✉️ {hasExistingRequests ? `Send Revision #${requestNumber}` : elakayAutoText("h_bacb1ed97615b0", "Send Your First Request")}
      </div>
      <div className="goc-form-sub">
        {hasExistingRequests
          ? elakayAutoText("h_5f578334a0a0a7", "Describe what you'd like changed or improved in the design.")
          : elakayAutoText("h_ff53bd68f80f09", "Tell the artist what you want — they'll create a custom design just for you.")}
      </div>

      <div className="goc-form-grid">

        {/* COL 1 */}
        <div className="goc-form-col">

          <div className="goc-form-field">
            <span className="goc-form-label">{text("t013", "Style Inspiration")}</span>
            <div className="goc-insp-grid">
              {INSPIRATION_STYLES.map(s => (
                <div key={s.label}
                  className={`goc-insp-card${form.inspirations.includes(s.label) ? " on" : ""}`}
                  onClick={() => toggleInspiration(s.label)}>
                  <span className="goc-insp-card-icon">{s.icon}</span>
                  <div className="goc-insp-card-text">
                    <div className="goc-insp-card-title">{s.label}</div>
                    <div className="goc-insp-card-desc">{s.desc}</div>
                  </div>
                  {form.inspirations.includes(s.label) && (
                    <span className="goc-insp-card-check">✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="goc-form-field">
            <label className="goc-form-label required">{text("t014", "General Instructions")}</label>
            <textarea className="goc-form-textarea" rows={4}
              value={form.specialInstructions}
              onChange={e => setF("specialInstructions", e.target.value)}
              placeholder={hasExistingRequests
                ? elakayAutoText("h_e2f61d0eee7d4b", "What changes do you want from the previous design?")
                : elakayAutoText("h_a44518f715643e", "Describe the design you want — theme, mood, message...")} />
            <div style={{ fontSize: 9.5, color: "var(--text-muted)", textAlign: "right" }}>
              {form.specialInstructions.length}{__elakayText("t096", "characters")}</div>
          </div>

          <div className="goc-form-field">
            <label className="goc-form-label">{text("t015", "Personal Message to the Artist")}</label>
            <textarea className="goc-form-textarea" rows={2}
              value={form.artistMessage}
              onChange={e => setF("artistMessage", e.target.value)}
              placeholder={text("t016", "Optional personal message...")} />
          </div>

          <div className="goc-form-row-2">
            <div className="goc-form-field">
              <label className="goc-form-label avoid">❌ {text("t017", "Avoid")}</label>
              <textarea className="goc-form-textarea avoid" rows={3}
                value={form.avoidInstructions}
                onChange={e => setF("avoidInstructions", e.target.value)}
                placeholder={__elakayText("t050", "Things you don't want...")} />
            </div>
            <div className="goc-form-field">
              <label className="goc-form-label ok">✅ {text("t018", "Required")}</label>
              <textarea className="goc-form-textarea ok" rows={3}
                value={form.requiredInstructions}
                onChange={e => setF("requiredInstructions", e.target.value)}
                placeholder={text("t019", "Must-have elements...")} />
            </div>
          </div>

          <div className="goc-form-row-2">
            <div className="goc-form-field">
              <label className="goc-form-label">{text("t020", "Desired Date")}</label>
              <input type="date" className="goc-form-input"
                value={form.desiredDate}
                onChange={e => setF("desiredDate", e.target.value)}
                min={new Date().toISOString().split("T")[0]} />
            </div>
            <div className="goc-form-field">
              <label className="goc-form-label">{text("t021", "Detail Level")}</label>
              <div className="goc-toggle-row">
                {DETAIL_LEVELS.map(d => (
                  <button key={d} type="button"
                    className={`goc-toggle-btn${form.detailLevel === d ? " active" : ""}`}
                    onClick={() => setF("detailLevel", d)}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="goc-form-field">
            <label className="goc-form-label">{text("t022", "Budget Range")}</label>
            <select className="goc-form-select"
              value={form.budget}
              onChange={e => setF("budget", e.target.value)}>
              <option value="">{text("t023", "— Select budget —")}</option>
              <option value="< $50">{"< $50"}</option>
              <option value="$50 - $100">$50 - $100</option>
              <option value="$100 - $200">$100 - $200</option>
              <option value="$200 - $500">$200 - $500</option>
              <option value="> $500">{"> $500"}</option>
            </select>
          </div>
        </div>

        {/* COL 2 */}
        <div className="goc-form-col">

          <div className="goc-form-field">
            <label className="goc-form-label">{text("t024", "Artistic Styles")}</label>
            <div className="goc-chips-wrap">
              {ARTISTIC_STYLES.map(s => (
                <button key={s} type="button"
                  className={`goc-chip${form.stylePrefs.includes(s) ? " on" : ""}`}
                  onClick={() => toggleStyle(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="goc-form-field">
            <label className="goc-form-label">📎 {text("t025", "Reference Images (up to 4)")}</label>
            <p style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.4, margin: 0 }}>{__elakayText("t051", "Upload images that inspire your vision.")}</p>
            <div className="goc-refs-grid">
              {form.images.map((r, i) => (
                <div key={i}
                  className={`goc-ref-slot${r ? " has" : ""}`}
                  onClick={() => {
                    setActiveRefIndex(i);
                    fileRef.current?.click();
                  }}>
                  {getRefSrc(r) ? (
                    <>
                      <img src={getRefSrc(r)} alt={elakayAutoText("h_f765b881ac0fd4", "Ref {value0}", { value0: i + 1 })} />
                      <button type="button" className="goc-ref-remove"
                        onClick={e => { e.stopPropagation(); handleRemoveRef(i); }}>
                        ✕
                      </button>
                    </>
                  ) : (
                    <div className="goc-ref-slot-empty">
                      <span className="goc-ref-slot-plus">+</span>
                      <span className="goc-ref-slot-label">{__elakayText("t097", "Ref")}{i + 1}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <input ref={fileRef} type="file" accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileSelect} />
          </div>

          {hasExistingRequests && (
            <div className="goc-form-info">
              <span>💡</span>
              <span>{__elakayText("t052", "Be specific about what you'd like changed from the previous design. The artist will use this to refine their work.")}</span>
            </div>
          )}

          {!isFormValid && (
            <div className="goc-form-warn">
              <span>⚠️</span>
              <span>{text("t026", "The \"General Instructions\" field is required.")}</span>
            </div>
          )}

        </div>
      </div>

      <div className="goc-form-actions">
        <button type="button" className="goc-btn goc-btn-ghost"
          onClick={onCancel} disabled={busy}>{__elakayText("t098", "Cancel")}</button>
        <button type="button" className="goc-btn goc-btn-gold"
          onClick={handleSubmit}
          disabled={!isFormValid || busy}>
          {busy ? elakayAutoText("h_932789836bda71", "⏳ Sending...") : `✉️ Send ${hasExistingRequests ? "Revision" : "Request"}`}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
export default function GraffitiOrderClient({
  order,
  infoSource,
  onClose = () => {},
  onAction = () => {}
}) {
  const text = (key, fallback) => getHardcodedText(infoSource, "graffitiOrderClient", key, fallback);

  const { token } = isAuthenticated();

  const product = useMemo(() => extractProduct(order), [order]);
  const graphic = product?.graphic;
  const sourceType = graphic?.sourceType;
  const orderId = order?.orders_id || order?._id;

  /* askSellerData = tableau */
  const [askRequests, setAskRequests] = useState(() => {
    const data = graphic?.askSellerData;
    if (!data) return [];
    if (Array.isArray(data)) return data.slice(0, MAX_ASK_REQUESTS);
    return [data];
  });

  /* ─── Groupes de designs soumis par le seller ───
     Chaque soumission de seller forme un "groupe" de slots.
     On les regroupe par submittedAt pour les afficher comme versions distinctes. */
  const sellerDesignGroups = useMemo(() => {
    const sellerSlots = (graphic?.slots || []).filter(s => s.graphicMade === "seller");
    if (sellerSlots.length === 0) return [];

    /* Pour l'instant, on regroupe tous les slots seller en UN seul groupe.
       Si tu veux multi-versions, il faudrait un champ "version" dans les slots
       ou stocker chaque version dans un tableau séparé côté backend. */
    return [{
      version: 1,
      submittedAt: graphic.submittedAt,
      graphicStatus: graphic.graphicStatus,
      notes: graphic.sellerNotes,
      slots: sellerSlots
    }];
  }, [graphic]);

  const hasSellerDesign = sellerDesignGroups.length > 0;
  const latestDesignGroup = sellerDesignGroups[sellerDesignGroups.length - 1];
  const isLatestPending = latestDesignGroup?.graphicStatus === "draft_ready";

  /* States */
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDesign, setPreviewDesign] = useState(null);
  const [showAskForm, setShowAskForm] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionBusyMode, setActionBusyMode] = useState(null);

  /* Tab actif — par défaut "request", mais si seller a soumis → "designs" */
  const [activeTab, setActiveTab] = useState(() => {
    if (hasSellerDesign && isLatestPending) return "designs";
    return "request";
  });

  /* Hook de sauvegarde */
  const save = useGraphicOrderSave({
    productID: product?.productID,
    authToken: token,
    pubToken: "",
    orderId: orderId,
    mode: "customer",
    enableAutoSave: false,
    infoSource
  });

  /* ─── Handler : envoyer une demande ─── */
  const handleSubmitAskRequest = useCallback(async (formData) => {
    if (askRequests.length >= MAX_ASK_REQUESTS) {
      setActionError(`Maximum ${MAX_ASK_REQUESTS} requests reached`);
      return;
    }

    setActionError(null);
    setActionResult(null);

    const newAsk = {
      specialInstructions: formData.specialInstructions,
      artistMessage: formData.artistMessage,
      avoidInstructions: formData.avoidInstructions,
      requiredInstructions: formData.requiredInstructions,
      desiredDate: formData.desiredDate || null,
      budget: formData.budget,
      detailLevel: formData.detailLevel,
      stylePrefs: formData.stylePrefs,
      inspirations: formData.inspirations,
      images: formData.images.filter(r => r)
    };

    const result = await save.appendAskRequest(newAsk, askRequests);

    if (result?.success) {
      setAskRequests(result.askRequests);
      setShowAskForm(false);
      setActionResult(
        askRequests.length === 0
          ? "Your request has been sent to the artist!"
          : `Revision #${askRequests.length + 1} sent to the artist!`
      );
      onAction({ type: "ask_appended", askRequests: result.askRequests });
    } else {
      setActionError(result?.error || "Failed to send request");
    }
  }, [askRequests, save, onAction]);

  /* ─── Handlers Approve / Reject ─── */
  const handleApprove = useCallback(async () => {
    const confirmed = window.confirm(
      __elakayText("t099", "Approve this design? Production will start and you won't be able to request changes.")
    );
    if (!confirmed) return;

    setActionBusy(true);
    setActionBusyMode("approve");
    setActionError(null);
    setActionResult(null);

    try {
      const result = await approveSellerDesign({ token, pubToken: "" }, orderId);
      if (result?.success === false) throw new Error(result.error || "Approval failed");
      setActionResult("Design approved! Production will start soon.");
      onAction({ type: "approved", result });
    } catch (err) {
      setActionError(err.message || "Failed to approve");
    } finally {
      setActionBusy(false);
      setActionBusyMode(null);
    }
  }, [orderId, token, onAction]);

  const handleReject = useCallback(async () => {
    const confirmed = window.confirm(__elakayText("t100", "Reject this design? This action is final."));
    if (!confirmed) return;

    setActionBusy(true);
    setActionBusyMode("reject");
    setActionError(null);
    setActionResult(null);

    try {
      const result = await rejectSellerDesign({ token, pubToken: "" }, orderId);
      if (result?.success === false) throw new Error(result.error || "Rejection failed");
      setActionResult("Design rejected.");
      onAction({ type: "rejected", result });
    } catch (err) {
      setActionError(err.message || "Failed to reject");
    } finally {
      setActionBusy(false);
      setActionBusyMode(null);
    }
  }, [orderId, token, onAction]);

  /* ─── Handler : ouvrir le DesignPreviewModal pour un groupe ─── */
  const handleViewDesign = useCallback((designGroup) => {
    /* Construire le design à partir des slots de ce groupe */
    const reconstructed = {
      _id: graphic.templateRef || `seller_design_${designGroup.submittedAt || Date.now()}`,
      label: `Artist's Design #${sellerDesignGroups.indexOf(designGroup) + 1}`,
      style: graphic.templateMeta?.style || "—",
      detail: graphic.templateMeta?.detail || "—",
      status: "published",
      slots: designGroup.slots.map((s, idx) => ({
        _id: s.originalSlotId || s.id || `slot_${idx}`,
        id: s.id || `slot_${idx}`,
        label: s.label || `Face ${idx + 1}`,
        url: s.url || "",
        graphicMade: s.graphicMade || "seller",
        slotState: {
          texts: s.slotState?.texts || [],
          layers: (s.slotState?.layers || []).map(l => ({
            _id: l.originalLayerId || null,
            urlImg: l.urlImg || "",
            mimeType: l.mimeType || "image/png",
            size: l.size,
            rotation: l.rotation,
            opacity: l.opacity,
            pos: l.pos || { x: 50, y: 50 }
          })),
          instructions: s.slotState?.instructions || ""
        }
      })),
      isLocal: false,
      isTemplate: false,
      sourceType: "ask",
      designGroup: designGroup
    };

    setPreviewDesign(reconstructed);
    setShowPreviewModal(true);
  }, [graphic, sellerDesignGroups]);

  /* ─── Reconstruction pour custom/template ─── */
  const customDesign = useMemo(() => {
    if (sourceType === "ask") return null;
    if (!graphic?.slots?.length) return null;

    const designId = graphic.templateRef || `local_${graphic.submittedAt || Date.now()}`;

    return {
      _id: designId,
      label: graphic.templateMeta?.label || (
        sourceType === "custom" ? "Your Custom Design" : "Template Design"
      ),
      style: graphic.templateMeta?.style || "—",
      detail: graphic.templateMeta?.detail || "—",
      status: "published",
      slots: graphic.slots.map((s, idx) => ({
        _id: s.originalSlotId || s.id || `slot_${idx}`,
        id: s.id || `slot_${idx}`,
        label: s.label || `Face ${idx + 1}`,
        url: s.url || "",
        graphicMade: s.graphicMade || "customer",
        slotState: {
          texts: s.slotState?.texts || [],
          layers: (s.slotState?.layers || []).map(l => ({
            _id: l.originalLayerId || null,
            urlImg: l.urlImg || "",
            mimeType: l.mimeType || "image/png",
            size: l.size,
            rotation: l.rotation,
            opacity: l.opacity,
            pos: l.pos || { x: 50, y: 50 }
          })),
          instructions: s.slotState?.instructions || ""
        }
      })),
      isLocal: sourceType === "custom",
      isTemplate: sourceType === "template",
      sourceType
    };
  }, [graphic, sourceType]);

  const sourceInfo = formatSourceTypeLabel(sourceType);
  const total = useMemo(() => {
    if (order?.amountTotal) return Number(order.amountTotal).toFixed(2);
    return ((product?.price || 0) * (product?.qty || 1)).toFixed(2);
  }, [order, product]);

  const canAddMoreRequests = askRequests.length < MAX_ASK_REQUESTS;

  /* Auto-clear notifications */
  useEffect(() => {
    if (actionResult || actionError) {
      const timer = setTimeout(() => {
        setActionResult(null);
        setActionError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [actionResult, actionError]);

  /* Pas de graphic */
  if (!graphic) {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: CSS + CSS_}} />
        <div className="goc-root">
          <div className="goc-hdr">
            <div className="goc-brand">
              <span className="goc-logo">ELAKAY</span>
              <span className="goc-oid">{orderId}</span>
            </div>
            <button type="button" className="goc-btn-close" onClick={onClose}>✕</button>
          </div>
          <div className="goc-empty-state">
            <div className="goc-empty-icon">📦</div>
            <div className="goc-empty-title">{text("t027", "Standard Order")}</div>
            <div className="goc-empty-sub">{__elakayText("t053", "This order does not contain any custom graphic customization.")}</div>
          </div>
          <div className="goc-footer">
            <div className="goc-footer-l"/>
            <div className="goc-footer-r">
              <button type="button" className="goc-btn goc-btn-ghost" onClick={onClose}>{text("t028", "Close")}</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ═══════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════ */
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS + CSS_ }} />

      <div className="goc-root">

        {/* Header */}
        <div className="goc-hdr">
          <div className="goc-brand">
            <span className="goc-logo">ELAKAY</span>
            <span className="goc-oid">{orderId}</span>
            <span className={`goc-badge ${graphic.graphicStatus || "pending"}`}>
              {formatStatusLabel(graphic.graphicStatus)}
            </span>
          </div>
          <button type="button" className="goc-btn-close" onClick={onClose}>✕</button>
        </div>

        {/* Source Banner */}
        <div className={`goc-source-banner ${sourceType || "unknown"}`}>
          <div className="goc-source-icon">{sourceInfo.icon}</div>
          <div className="goc-source-info">
            <div className="goc-source-title">{sourceInfo.label}</div>
            <div className="goc-source-sub">{sourceInfo.sub}</div>
            {graphic.templateMeta?.label && (
              <div className="goc-source-meta">{__elakayText("t054", "Template:")}<strong>{graphic.templateMeta.label}</strong>
              </div>
            )}
          </div>
          <div className="goc-source-actions">
            {(sourceType === "custom" || sourceType === "template") && customDesign && (
              <button type="button"
                className="goc-btn goc-btn-teal goc-btn-sm"
                onClick={() => { setPreviewDesign(customDesign); setShowPreviewModal(true); }}>{__elakayText("t049", "👁️ View Design")}</button>
            )}
          </div>
        </div>

        {/* TABS (mode ask uniquement) */}
        {sourceType === "ask" && (
          <div className="goc-tabs">
            <button type="button"
              className={`goc-tab${activeTab === "request" ? " on" : ""}`}
              onClick={() => setActiveTab("request")}>{__elakayText("t055", "💬 Client Request")}<span className="goc-tab-badge gold">
                {askRequests.length}/{MAX_ASK_REQUESTS}
              </span>
            </button>
            <button type="button"
              className={`goc-tab${activeTab === "designs" ? " on" : ""}`}
              onClick={() => setActiveTab("designs")}>{__elakayText("t056", "🎨 Seller Designs")}{sellerDesignGroups.length === 0 ? (
                <span className="goc-tab-badge empty">{text("t029", "Waiting")}</span>
              ) : isLatestPending ? (
                <span className="goc-tab-badge purple">⚡ {text("t030", "Action")}</span>
              ) : graphic.graphicStatus === "approved" ? (
                <span className="goc-tab-badge ok">{text("t031", "Approved")}</span>
              ) : graphic.graphicStatus === "rejected" ? (
                <span className="goc-tab-badge warn">{text("t032", "Rejected")}</span>
              ) : (
                <span className="goc-tab-badge teal">{sellerDesignGroups.length}</span>
              )}
            </button>
          </div>
        )}

        {/* Counter strip (mode ask uniquement) */}
        {sourceType === "ask" && activeTab === "request" && (
          <div className="goc-counter-strip">
            <span className="goc-counter-label">{text("t033", "Requests:")}</span>
            <div className="goc-counter-dots">
              {[...Array(MAX_ASK_REQUESTS)].map((_, i) => {
                const used = i < askRequests.length;
                const current = i === askRequests.length && canAddMoreRequests;
                return (
                  <div key={i}
                    className={`goc-counter-dot ${
                      used ? "used" : current ? "current" : "empty"
                    }`}
                  />
                );
              })}
            </div>
            <span className="goc-counter-text">
              <strong>{askRequests.length}</strong> / {MAX_ASK_REQUESTS}
            </span>
            {askRequests.length === MAX_ASK_REQUESTS - 1 && (
              <span className="goc-counter-warn">⚠ {text("t034", "Last revision available")}</span>
            )}
            {askRequests.length === MAX_ASK_REQUESTS && (
              <span className="goc-counter-warn">🚫 {text("t035", "Limit reached")}</span>
            )}
          </div>
        )}

        {/* Action status */}
        {(actionResult || actionError) && (
          <div className={`goc-action-status ${actionError ? "error" : "success"}`}>
            <span className="goc-action-icon">
              {actionError ? "⚠️" : "✓"}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {actionError || actionResult}
            </span>
            <button type="button"
              className="goc-btn goc-btn-ghost goc-btn-sm"
              onClick={() => { setActionResult(null); setActionError(null); }}>
              ✕
            </button>
          </div>
        )}

        {/* Body */}
        <div className="goc-body">
          <div className="goc-content">

            {/* MODE CUSTOM/TEMPLATE → simple display */}
            {(sourceType === "custom" || sourceType === "template") && (
              <div className="goc-empty-state">
                <div className="goc-empty-icon">
                  {sourceType === "custom" ? "🎨" : "📋"}
                </div>
                <div className="goc-empty-title">
                  {sourceType === "custom" ? elakayAutoText("h_c6ba5f40e785ab", "Your Custom Design") : elakayAutoText("h_5c6e51851db68c", "Your Template Design")}
                </div>
                <div className="goc-empty-sub">
                  {sourceType === "custom"
                    ? `You designed ${graphic.slots?.length || 0} face(s) for this order.`
                    : `Template "${graphic.templateMeta?.label || ''}" with ${graphic.slots?.length || 0} face(s).`}
                </div>
                <button type="button"
                  className="goc-btn goc-btn-teal"
                  onClick={() => { setPreviewDesign(customDesign); setShowPreviewModal(true); }}>{__elakayText("t057", "👁️ View Your Design")}</button>
              </div>
            )}

            {/* MODE ASK */}
            {sourceType === "ask" && (
              <>
                {/* TAB: Client Request */}
                {activeTab === "request" && (
                  <>
                    {/* Liste des demandes existantes */}
                    {askRequests.length > 0 && (
                      <div className="goc-section">
                        <div className="goc-section-title">{__elakayText("t058", "📜 Your Requests (")}{askRequests.length})
                        </div>
                        <div className="goc-section-sub">{__elakayText("t059", "Click on a request to see its details.")}</div>
                        <div className="goc-request-cards">
                          {askRequests.map((askReq, i) => (
                            <AskRequestCard
                              key={i}
                              askRequest={askReq}
                              index={i}
                              infoSource={infoSource}
                              expanded={expandedRequest === i}
                              onToggle={() => setExpandedRequest(expandedRequest === i ? null : i)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Formulaire si on peut encore en ajouter */}
                    {canAddMoreRequests && (
                      <>
                        {showAskForm ? (
                          <AskForm
                            requestNumber={askRequests.length + 1}
                            onSubmit={handleSubmitAskRequest}
                            onCancel={() => setShowAskForm(false)}
                            busy={save.busy && save.busyMode === "ask"}
                            hasExistingRequests={askRequests.length > 0}
                            infoSource={infoSource}
                          />
                        ) : (
                          askRequests.length === 0 ? (
                            <div className="goc-cta-banner">
                              <div className="goc-cta-banner-icon">📝</div>
                              <div className="goc-cta-banner-title">{text("t036", "Ready to Start?")}</div>
                              <div className="goc-cta-banner-sub">{__elakayText("t060", "Send your first request to the artist with all the details you have in mind.")}</div>
                              <button type="button"
                                className="goc-btn goc-btn-gold"
                                onClick={() => setShowAskForm(true)}>{__elakayText("t061", "✉️ Send Your First Request")}</button>
                            </div>
                          ) : (
                            <div className="goc-revision-cta">
                              <div className="goc-revision-cta-text">{__elakayText("t062", "Not satisfied? You can send")}{MAX_ASK_REQUESTS - askRequests.length}{__elakayText("t063", "more revision")}{MAX_ASK_REQUESTS - askRequests.length > 1 ? "s" : ""}.
                              </div>
                              <button type="button"
                                className="goc-btn goc-btn-warn"
                                onClick={() => setShowAskForm(true)}>{__elakayText("t064", "✎ Send Revision #")}{askRequests.length + 1}
                              </button>
                            </div>
                          )
                        )}
                      </>
                    )}

                    {/* Limit reached */}
                    {!canAddMoreRequests && (
                      <div className="goc-limit-reached">
                        <div className="goc-limit-icon">🚫</div>
                        <div className="goc-limit-title">{text("t037", "Request Limit Reached")}</div>
                        <div className="goc-limit-sub">{__elakayText("t065", "You've sent the maximum of")}{MAX_ASK_REQUESTS}{__elakayText("t066", "requests for this order. The artist is working on your design.")}</div>
                      </div>
                    )}
                  </>
                )}

                {/* TAB: Seller Designs */}
                {activeTab === "designs" && (
                  <>
                    {sellerDesignGroups.length === 0 ? (
                      <div className="goc-empty-state">
                        <div className="goc-empty-icon">⏳</div>
                        <div className="goc-empty-title">{text("t038", "No Design Yet")}</div>
                        <div className="goc-empty-sub">{__elakayText("t067", "The artist hasn't submitted a design yet. You'll be notified by email when it's ready.")}</div>
                        {canAddMoreRequests && askRequests.length === 0 && (
                          <button type="button"
                            className="goc-btn goc-btn-gold"
                            onClick={() => setActiveTab("request")}>{__elakayText("t068", "✉️ Send Your Request")}</button>
                        )}
                      </div>
                    ) : (
                      <div className="goc-section">
                        <div className="goc-section-title">{__elakayText("t069", "🎨 Designs Submitted (")}{sellerDesignGroups.length})
                        </div>
                        <div className="goc-section-sub">{__elakayText("t070", "Click \"View Design\" to see each submission in detail.")}{isLatestPending && elakayAutoText("h_6991ab345cb032", "Review the latest design and approve or reject it.")}
                        </div>

                        <div className="goc-design-cards">
                          {sellerDesignGroups.map((group, i) => (
                            <SellerDesignCard
                              key={i}
                              designGroup={group}
                              index={i}
                              isLatest={i === sellerDesignGroups.length - 1}
                              isPending={i === sellerDesignGroups.length - 1 && isLatestPending}
                              onView={handleViewDesign}
                              onApprove={handleApprove}
                              onReject={handleReject}
                              busy={actionBusy}
                              busyMode={actionBusyMode}
                              infoSource={infoSource}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

              </>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="goc-footer">
          <div className="goc-footer-l">
            <div>
              <span className="goc-price-label">{text("t039", "Order Total")}</span>
              <div className="goc-price-value">${total}</div>
            </div>
            {(order?.prod_name || product) && (
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                {order?.prod_name || product?.name || elakayAutoText("h_dd3b86d1ef21b9", "Product")}
                {product?.variant && ` · ${product.variant}`}
                {product?.qty && ` · ×${product.qty}`}
              </div>
            )}
          </div>
          <div className="goc-footer-r">

            {/* Quick switch entre tabs */}
            {sourceType === "ask" && activeTab === "request" && hasSellerDesign && (
              <button type="button" className="goc-btn goc-btn-purple"
                onClick={() => setActiveTab("designs")}>{__elakayText("t071", "🎨 View Designs →")}</button>
            )}
            {sourceType === "ask" && activeTab === "designs" && canAddMoreRequests && (
              <button type="button" className="goc-btn goc-btn-warn"
                onClick={() => setActiveTab("request")}>{__elakayText("t072", "← Back to Requests")}</button>
            )}

            <button type="button" className="goc-btn goc-btn-ghost" onClick={onClose}>
              {text("t028", "Close")}
            </button>
          </div>
        </div>

      </div>

      {/* DesignPreviewModal */}
      {showPreviewModal && previewDesign && (
        <DesignPreviewModal
          design={previewDesign}
          onClose={() => { setShowPreviewModal(false); setPreviewDesign(null); }}
          showEditButton={false}
        />
      )}

    </>
  );
}
